import type { Artifact } from '../../../api/discoveryAgent';

export type HtmlSegment = { type: 'html'; value: string };
export type ArtifactCardSegment = { type: 'artifact-card'; artifact: Artifact };
export type IdeasListSegment = {
  type: 'ideas-list';
  ideaIds: string[];
  artifactId?: string;
};
export type ExploreMessageSegment = HtmlSegment | ArtifactCardSegment | IdeasListSegment;

const ARTIFACT_MARKER_REGEX =
  /<span[^>]*\bdata-artifact-(end-id|end)="([^"]+)"[^>]*>\s*<\/span>/gi;

const IDEAS_LIST_SPAN_REGEX =
  /<span[^>]*\bdata-idea-ids="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi;

export function normalizeIdeasListMarkerParagraphs(html: string): string {
  return html.replace(
    /<p(\s[^>]*)?>\s*(<span[^>]*\bdata-idea-ids="[^"]+"[^>]*>[\s\S]*?<\/span>)\s*<\/p>/gi,
    '$2'
  );
}

function parseIdeaIdsFromAttribute(idsString: string): string[] {
  return idsString
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function buildOrderedIdeaIdsSignature(ideaIds: string[]): string {
  return ideaIds.join(',');
}

function extractArtifactIdFromSpanHtml(spanHtml: string): string | undefined {
  const match = spanHtml.match(/\bdata-artifact-id="([^"]+)"/);
  return match?.[1]?.trim() || undefined;
}

function resolveIdeasListArtifactIdFromArtifacts(
  ideaIds: string[],
  artifacts: Artifact[] | undefined
): string | undefined {
  if (!artifacts?.length) {
    return undefined;
  }

  const signature = buildOrderedIdeaIdsSignature(ideaIds);
  const matched = artifacts.find((artifact) => {
    if (artifact.type !== 'ideas_list' || !artifact.idea_ids?.length) {
      return false;
    }
    return buildOrderedIdeaIdsSignature(artifact.idea_ids) === signature;
  });

  return matched?._id;
}

type MarkerMatch =
  | { index: number; length: number; kind: 'artifact'; artifact: Artifact }
  | { index: number; length: number; kind: 'ideas-list'; ideaIds: string[]; artifactId?: string };

function collectArtifactMarkers(
  htmlContent: string,
  artifacts: Artifact[] | undefined
): MarkerMatch[] {
  if (!artifacts?.length) {
    return [];
  }

  const artifactsByAlias = new Map(artifacts.map((artifact) => [artifact.alias, artifact]));
  const artifactsById = new Map(
    artifacts.filter((artifact) => artifact._id).map((artifact) => [artifact._id as string, artifact])
  );
  const markers: MarkerMatch[] = [];

  for (const match of htmlContent.matchAll(ARTIFACT_MARKER_REGEX)) {
    const kind = match[1] as 'end' | 'end-id';
    const key = match[2];
    const artifact = kind === 'end-id' ? artifactsById.get(key) : artifactsByAlias.get(key);
    if (!artifact || match.index === undefined) {
      continue;
    }
    markers.push({
      index: match.index,
      length: match[0].length,
      kind: 'artifact',
      artifact,
    });
  }

  return markers;
}

function collectIdeasListMarkers(
  htmlContent: string,
  artifacts: Artifact[] | undefined
): MarkerMatch[] {
  const markers: MarkerMatch[] = [];

  for (const match of htmlContent.matchAll(IDEAS_LIST_SPAN_REGEX)) {
    const idsString = match[1];
    if (!idsString || match.index === undefined) {
      continue;
    }
    const ideaIds = parseIdeaIdsFromAttribute(idsString);
    if (ideaIds.length === 0) {
      continue;
    }

    const spanHtml = match[0];
    const artifactId =
      extractArtifactIdFromSpanHtml(spanHtml) ??
      resolveIdeasListArtifactIdFromArtifacts(ideaIds, artifacts);

    markers.push({
      index: match.index,
      length: match[0].length,
      kind: 'ideas-list',
      ideaIds,
      artifactId,
    });
  }

  return markers;
}

export function buildExploreMessageSegments(
  htmlContent: string,
  artifacts?: Artifact[]
): ExploreMessageSegment[] {
  const markers = [
    ...collectArtifactMarkers(htmlContent, artifacts),
    ...collectIdeasListMarkers(htmlContent, artifacts),
  ].sort((left, right) => left.index - right.index);

  if (markers.length === 0) {
    return [{ type: 'html', value: htmlContent }];
  }

  const segments: ExploreMessageSegment[] = [];
  let lastEnd = 0;

  for (const marker of markers) {
    const htmlBefore = htmlContent.slice(lastEnd, marker.index);
    if (htmlBefore) {
      segments.push({ type: 'html', value: htmlBefore });
    }

    if (marker.kind === 'artifact') {
      segments.push({ type: 'artifact-card', artifact: marker.artifact });
    } else {
      segments.push({
        type: 'ideas-list',
        ideaIds: marker.ideaIds,
        artifactId: marker.artifactId,
      });
    }

    lastEnd = marker.index + marker.length;
  }

  if (lastEnd < htmlContent.length) {
    segments.push({ type: 'html', value: htmlContent.slice(lastEnd) });
  }

  return segments;
}

export function buildIdeasListStateKey(
  messageId: string,
  segmentIndex: number,
  artifactId?: string
): string {
  if (artifactId?.trim()) {
    return artifactId.trim();
  }
  return `legacy:${messageId}:${segmentIndex}`;
}
